// wingNativePdfExport.ts - Produce the Wing Analysis report by MERGING the
// native CADViewer Quick PDF (cvjs_publishPDF) with generated cover + table
// pages, then download the merged PDF directly.
//
// The CADViewer library bundles jsPDF and downloads the published PDF via a
// classic blob anchor (createObjectURL + <a download> + .click()). We briefly
// intercept that anchor click to capture the native PDF bytes, then merge them
// with pdf-lib.

import { jsPDF } from "jspdf";
import { PDFDocument } from "pdf-lib";
import {
  WingPdfParams,
  NAVY,
  drawWingTable,
  drawLogoBanner,
  drawHeaderSeparator,
} from "./wingPdfExport";

/** Trigger the native publish (sets filename + calls cvjs_publishPDF). */
export type PublishFn = (fileName: string) => void;

/**
 * Intercept the next blob anchor download whose filename ends in `.pdf` and
 * resolve with its bytes. Suppresses that native download (we re-download the
 * merged result instead). Restores the patched method afterwards.
 */
const LOG = "[WingNativePDF]";

function captureNextPdfDownload(timeoutMs = 25000): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const proto = HTMLAnchorElement.prototype;
    const originalClick = proto.click;
    const eventProto = EventTarget.prototype;
    const originalDispatch = eventProto.dispatchEvent;
    const originalCreateObjectURL = URL.createObjectURL.bind(URL);
    let settled = false;
    let captured: ArrayBuffer | null = null;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      proto.click = originalClick;
      eventProto.dispatchEvent = originalDispatch;
      URL.createObjectURL = originalCreateObjectURL;
      clearTimeout(timer);
      if (graceTimer) clearTimeout(graceTimer);
    };

    const finish = (buf: ArrayBuffer, via: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      console.log(`${LOG} captured native PDF (${buf.byteLength} bytes) via ${via}`);
      resolve(buf);
    };

    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        cleanup();
        console.warn(`${LOG} timed out waiting for the native published PDF`);
        reject(new Error("Timed out waiting for the native published PDF."));
      }
    }, timeoutMs);

    // PRIMARY: jsPDF builds the PDF Blob and calls URL.createObjectURL on it,
    // regardless of how the download is then triggered. Capture the bytes here,
    // then briefly wait for the anchor download (so we can suppress the native
    // one); if none comes (e.g. window.open path), settle anyway.
    URL.createObjectURL = function (obj: any) {
      const url = originalCreateObjectURL(obj);
      try {
        const type = (obj && obj.type ? String(obj.type) : "").toLowerCase();
        if (!settled && !captured && obj && typeof obj.arrayBuffer === "function" && type.includes("pdf")) {
          obj
            .arrayBuffer()
            .then((b: ArrayBuffer) => {
              if (settled) return;
              captured = b;
              if (!graceTimer) {
                graceTimer = setTimeout(() => {
                  if (captured) finish(captured, "createObjectURL");
                }, 1500);
              }
            })
            .catch(() => {});
        }
      } catch (_e) {
        /* ignore */
      }
      return url;
    } as typeof URL.createObjectURL;

    // Recognise + suppress the native blob anchor that downloads the PDF. The
    // CADViewer/jsPDF FileSaver path triggers it via EITHER a.click() OR
    // a.dispatchEvent(clickEvent) depending on browser support — handle both.
    const handlePdfAnchor = (el: any): boolean => {
      try {
        if (!(el instanceof HTMLAnchorElement)) return false;
        const dl = (el.getAttribute("download") || el.download || "")
          .toString()
          .toLowerCase();
        if (!dl.endsWith(".pdf") || !el.href) return false;
        console.log(`${LOG} suppressed native .pdf anchor download (${dl})`);
        if (captured) {
          finish(captured, "createObjectURL+suppressed");
        } else if (!settled) {
          fetch(el.href)
            .then((r) => r.arrayBuffer())
            .then((b) => finish(b, "anchorFetch"))
            .catch(() => {});
        }
        return true; // suppress
      } catch (_e) {
        return false;
      }
    };

    proto.click = function (this: HTMLAnchorElement) {
      if (handlePdfAnchor(this)) return;
      return originalClick.apply(this, arguments as any);
    };

    eventProto.dispatchEvent = function (this: EventTarget, event: Event) {
      if (event && event.type === "click" && handlePdfAnchor(this)) {
        return false; // suppress the synthetic click that triggers the download
      }
      return originalDispatch.apply(this, arguments as any);
    };
  });
}

/** Cover page: title + property info + the full wing space list. */
function buildCoverBytes(params: WingPdfParams): ArrayBuffer {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;
  let y = margin;

  y = drawLogoBanner(doc, params.logos, margin, y);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Wing Analysis", margin, y);
  y += 24;

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);
  const lines = [
    params.propertyName &&
      `Property: ${params.propertyName}${
        params.propertyNumber ? ` (${params.propertyNumber})` : ""
      }`,
    params.planName && `Plan: ${params.planName}`,
    `Generated: ${new Date().toLocaleString()}`,
  ].filter(Boolean) as string[];
  lines.forEach((t) => {
    doc.text(t, margin, y, { maxWidth: pageW - margin * 2 });
    y += 16;
  });

  // Gray separator between the header and the content.
  y = drawHeaderSeparator(doc, margin, y);
  y += 4;
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text(`Wing Spaces (${params.spaces.length})`, margin, y);
  y += 18;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(40, 40, 40);
  params.spaces.forEach((s) => {
    if (y > pageH - margin) {
      doc.addPage();
      y = margin;
    }
    doc.text(`${s.number}${s.tenant ? `   —   ${s.tenant}` : ""}`, margin, y);
    y += 15;
  });

  return doc.output("arraybuffer");
}

/** Results table page(s). */
function buildTableBytes(params: WingPdfParams): ArrayBuffer {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  drawWingTable(doc, params.tableData, 36, {
    propertyName: params.propertyName,
    planName: params.planName,
    logos: params.logos,
  });
  return doc.output("arraybuffer");
}

async function downloadBytes(bytes: Uint8Array, fileName: string) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Build the merged Wing Analysis report: cover + native plan PDF + table.
 * `publish` must set the (sentinel) filename and call cvjs_publishPDF; this
 * function captures the resulting native PDF and merges it. Throws on failure
 * so the caller can fall back to the client-side composed PDF.
 */
export async function exportWingPlanPdfNative(
  params: WingPdfParams,
  publish: PublishFn
): Promise<void> {
  // Unique sentinel name; the interception matches on `.pdf` regardless, but a
  // unique name avoids colliding with any unrelated download.
  const sentinel = `wing_native_${new Date().getTime()}.pdf`;

  // Arm the interception BEFORE triggering the publish.
  const capture = captureNextPdfDownload();
  console.log(`${LOG} interception armed; triggering native publish…`);
  publish(sentinel);
  const nativeBytes = await capture;

  // Generate the extra pages (A4 landscape).
  const coverBytes = buildCoverBytes(params);
  const tableBytes = buildTableBytes(params);

  const merged = await PDFDocument.create();
  const native = await PDFDocument.load(nativeBytes);
  const cover = await PDFDocument.load(coverBytes);
  const table = await PDFDocument.load(tableBytes);

  // Uniform page size for the whole report (A4 landscape, points).
  const A4_W = 841.89;
  const A4_H = 595.28;

  // 1) Cover (header + property + spaces), 2) Results table.
  for (const p of await merged.copyPages(cover, cover.getPageIndices())) {
    merged.addPage(p);
  }
  for (const p of await merged.copyPages(table, table.getPageIndices())) {
    merged.addPage(p);
  }

  // 3) Native plan page(s) LAST — scaled to fit an A4 landscape page so every
  //    page in the report shares the same proportions (the raw Quick PDF sheet
  //    is much larger than A4).
  const nativePages = await merged.embedPdf(native, native.getPageIndices());
  for (const ep of nativePages) {
    const page = merged.addPage([A4_W, A4_H]);
    const margin = 18;
    const scale = Math.min(
      (A4_W - margin * 2) / ep.width,
      (A4_H - margin * 2) / ep.height
    );
    const w = ep.width * scale;
    const h = ep.height * scale;
    page.drawPage(ep, {
      x: (A4_W - w) / 2,
      y: (A4_H - h) / 2,
      width: w,
      height: h,
    });
  }

  const out = await merged.save();
  console.log(
    `${LOG} merged report: cover ${cover.getPageCount()} + table ${table.getPageCount()} + native ${native.getPageCount()} = ${merged.getPageCount()} pages (uniform A4 landscape, plan last)`
  );
  const safeName = (params.propertyName || "plan").replace(/\s+/g, "_");
  await downloadBytes(out, params.fileName || `Wing_Analysis_${safeName}.pdf`);
  console.log(`${LOG} merged PDF downloaded.`);
}
